import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import PayrollLayout from '../../components/layout/PayrollLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Coffee, Download, Loader2, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtBreak = (min) => `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m`;

const buildMonthOptions = () => {
  const out = [];
  const now = new Date();
  let y = 2026;
  let m = 4;
  while (y < now.getFullYear() || (y === now.getFullYear() && m <= now.getMonth() + 1)) {
    const value = `${y}-${String(m).padStart(2, '0')}`;
    out.push({ value, label: new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) });
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return out.reverse();
};

// Total break time per employee since their first clock-in — same clock
// pairing and automatic break rules as Holiday Entitlement. Read-only.
export default function BreaksPage({ payrollMode = false }) {
  const Layout = payrollMode ? PayrollLayout : AdminLayout;
  const monthOptions = useMemo(buildMonthOptions, []);
  const [mode, setMode] = useState('total'); // 'total' | 'month'
  const [month, setMonth] = useState(() => buildMonthOptions()[0]?.value || '2026-04');
  const [data, setData] = useState({ rows: [], period: '' });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [downloading, setDownloading] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [monthsCache, setMonthsCache] = useState({});
  const [monthsLoading, setMonthsLoading] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = mode === 'month' ? { month } : {};
      const r = await axios.get(`${API}/admin/breaks-summary`, { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load breaks summary');
    }
    setLoading(false);
  }, [mode, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleExpand = async (employeeId) => {
    if (expandedId === employeeId) { setExpandedId(null); return; }
    setExpandedId(employeeId);
    if (!monthsCache[employeeId]) {
      setMonthsLoading(employeeId);
      try {
        const r = await axios.get(`${API}/admin/breaks-summary/employee/${employeeId}/months`);
        setMonthsCache((m) => ({ ...m, [employeeId]: r.data.months || [] }));
      } catch {
        toast.error('Failed to load monthly breakdown');
      }
      setMonthsLoading(null);
    }
  };

  const downloadExcel = async (employeeId = null, name = '', monthOverride = null) => {
    const dlKey = employeeId ? `${employeeId}${monthOverride ? `-${monthOverride}` : ''}` : 'all';
    setDownloading(dlKey);
    try {
      const params = {};
      if (monthOverride) params.month = monthOverride;
      else if (mode === 'month') params.month = month;
      if (employeeId) params.employee_id = employeeId;
      const r = await axios.get(`${API}/admin/breaks-summary/export.xlsx`, { params, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      const suffix = monthOverride || (mode === 'month' ? month : 'all_time');
      a.download = employeeId
        ? `Breaks_${name.replace(/\s+/g, '_')}_${suffix}.xlsx`
        : `Breaks_${suffix}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
    setDownloading(null);
  };

  const filtered = data.rows.filter((r) => !q.trim() || (r.name || '').toLowerCase().includes(q.trim().toLowerCase()));
  const totBreakMin = filtered.reduce((a, r) => a + (r.break_minutes || 0), 0);
  const totGross = filtered.reduce((a, r) => a + (r.gross_hours || 0), 0);
  const totNet = filtered.reduce((a, r) => a + (r.net_hours || 0), 0);

  return (
    <Layout>
      <div className="p-4 sm:p-6 space-y-5" data-testid="breaks-page">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
          <div>
            <h1 className="text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
              <Coffee className="w-6 h-6 text-primary" strokeWidth={1.5} />
              BREAKS
            </h1>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              Total break time per employee from Clock-In History, since their first clock-in.
              Automatic rules: &lt;4h = 0m · 4–6h = 20m · 6–8h = 30m · &gt;8h = 60m (Primary Admin overrides respected).
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => downloadExcel()}
            disabled={downloading === 'all' || filtered.length === 0}
            className="h-8 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white"
            data-testid="breaks-export-all"
          >
            {downloading === 'all' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1.5" /> {mode === 'month' ? 'Month Excel' : 'Full Excel'}</>}
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-sm border border-border overflow-hidden">
            {['total', 'month'].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold transition-colors ${mode === m ? 'bg-primary text-black' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid={`breaks-mode-${m}`}
              >
                {m === 'total' ? 'Total · All time' : 'Monthly'}
              </button>
            ))}
          </div>
          {mode === 'month' && (
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-44 h-8 rounded-sm text-xs" data-testid="breaks-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border max-h-64">
                {monthOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee name…"
              className="pl-8 h-8 rounded-sm text-xs" data-testid="breaks-search" />
          </div>
        </div>

        <p className="text-xs uppercase tracking-wider text-muted-foreground" data-testid="breaks-period-label">
          {data.period}{filtered.length ? ` · ${filtered.length} employees` : ''}
        </p>

        {loading ? (
          <div className="py-16 text-center text-muted-foreground animate-pulse">Loading…</div>
        ) : filtered.length === 0 ? (
          <Card className="border-border"><CardContent className="py-12 text-center text-sm text-muted-foreground">
            No clock records found for this period.
          </CardContent></Card>
        ) : (
          <div className="space-y-2">
            <Card className="border-amber-500/40 bg-amber-500/5">
              <CardContent className="p-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
                <span className="text-muted-foreground text-xs uppercase tracking-wider">Totals</span>
                <span data-testid="breaks-total-break"><b className="text-amber-400">{fmtBreak(totBreakMin)}</b> breaks</span>
                <span data-testid="breaks-total-gross"><b>{totGross.toFixed(2)}</b> h gross</span>
                <span data-testid="breaks-total-net"><b className="text-emerald-400">{totNet.toFixed(2)}</b> h net</span>
              </CardContent>
            </Card>
            {filtered.map((r) => (
              <Card key={r.employee_id} className="border-border" data-testid={`breaks-row-${r.employee_id}`}>
                <CardContent className="p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggleExpand(r.employee_id)}
                      className="min-w-0 flex-1 basis-full sm:basis-auto text-left group"
                      data-testid={`breaks-name-${r.employee_id}`}
                      title="Show monthly breakdown"
                    >
                      <p className="text-sm font-medium truncate group-hover:text-primary flex items-center gap-1.5">
                        {expandedId === r.employee_id
                          ? <ChevronDown className="w-3.5 h-3.5 text-primary shrink-0" />
                          : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                        <span className="truncate underline-offset-2 group-hover:underline">{r.name}</span>
                      </p>
                      <p className="text-[11px] text-muted-foreground pl-5">
                        Breaks: <b className="text-amber-400">{fmtBreak(r.break_minutes)}</b>
                        <span className="mx-1.5">·</span>
                        {r.shifts} shift{r.shifts === 1 ? '' : 's'}
                        <span className="mx-1.5">·</span>
                        Gross: <b className="text-foreground">{r.gross_hours.toFixed(2)} h</b>
                        <span className="mx-1.5">·</span>
                        Net: <b className="text-emerald-400">{r.net_hours.toFixed(2)} h</b>
                      </p>
                    </button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadExcel(r.employee_id, r.name)}
                      disabled={downloading === r.employee_id}
                      className="h-7 px-2 rounded-sm text-[11px]"
                      data-testid={`breaks-export-${r.employee_id}`}
                    >
                      {downloading === r.employee_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Download className="w-3.5 h-3.5 mr-1" /> Excel</>}
                    </Button>
                  </div>
                  {expandedId === r.employee_id && (
                    <div className="mt-2 ml-5 border-l-2 border-border pl-3 space-y-1.5" data-testid={`breaks-months-${r.employee_id}`}>
                      {monthsLoading === r.employee_id ? (
                        <p className="text-xs text-muted-foreground animate-pulse py-1">Loading months…</p>
                      ) : (monthsCache[r.employee_id] || []).length === 0 ? (
                        <p className="text-xs text-muted-foreground py-1">No monthly records found.</p>
                      ) : (
                        (monthsCache[r.employee_id] || []).map((m) => (
                          <div key={m.month} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-0.5" data-testid={`breaks-month-row-${r.employee_id}-${m.month}`}>
                            <span className="text-xs font-medium w-28">{m.label}</span>
                            <span className="text-[11px] text-muted-foreground">
                              Break: <b className="text-amber-400">{fmtBreak(m.break_minutes)}</b>
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {m.shifts} shift{m.shifts === 1 ? '' : 's'}
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              Gross: <b className="text-foreground">{m.gross_hours.toFixed(2)} h</b>
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              Net: <b className="text-emerald-400">{m.net_hours.toFixed(2)} h</b>
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => downloadExcel(r.employee_id, r.name, m.month)}
                              disabled={downloading === `${r.employee_id}-${m.month}`}
                              className="h-6 px-1.5 rounded-sm text-[10px] text-emerald-400 hover:text-emerald-300"
                              data-testid={`breaks-month-export-${r.employee_id}-${m.month}`}
                            >
                              {downloading === `${r.employee_id}-${m.month}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Download className="w-3 h-3 mr-1" /> Excel</>}
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
